// @ts-check

if (!customElements.get('ff-blog-pagination')) {
  class FfBlogPagination extends HTMLElement {
    /** @type {IntersectionObserver|null} */
    ffInfiniteScrollObserver = null;
    /** @type {boolean} */
    ffIsLoadingMore = false;
    /** @type {AbortController|null} */
    ffAbortController = null;

    constructor() {
      super();
      const mode = this.dataset.ffPaginationMode;

      if (mode === 'infinite') {
        this.ffInitInfiniteScroll();
      }

      this.ffInitPaginationLinks();
    }

    // ── Infinite Scroll ──────────────────────────────────────

    ffInitInfiniteScroll = () => {
      this.ffInfiniteScrollObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.ffIsLoadingMore) {
              this.ffLoadMoreArticles();
            }
          });
        },
        { rootMargin: '800px' }
      );
      this.ffObserveSentinel();
    };

    ffObserveSentinel = () => {
      if (!this.ffInfiniteScrollObserver) return;
      this.ffInfiniteScrollObserver.disconnect();

      /** @type {HTMLElement|null} */
      const sentinel = this.querySelector(
        '.ff-blog-infinite-sentinel[data-ff-next-page]'
      );
      if (sentinel) {
        this.ffInfiniteScrollObserver.observe(sentinel);
      }
    };

    ffLoadMoreArticles = async () => {
      /** @type {HTMLElement|null} */
      const sentinel = this.querySelector('.ff-blog-infinite-sentinel');
      if (!sentinel || !sentinel.dataset.ffNextPage || this.ffIsLoadingMore) return;

      this.ffIsLoadingMore = true;
      sentinel.classList.add('ff-loading');

      try {
        const nextPageUrl = new URL(sentinel.dataset.ffNextPage, window.location.origin);
        const response = await fetch(nextPageUrl);
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html');

        // Scope by block ID to handle multiple blog blocks on the same page
        const thisId = this.id;
        /** @type {HTMLElement|null} */
        const newBlock = html.getElementById(thisId);
        /** @type {HTMLElement|null} */
        const newGrid = newBlock?.querySelector('.ff-blog-grid-js') ?? null;
        /** @type {HTMLElement|null} */
        const currentGrid = this.querySelector('.ff-blog-grid-js');

        if (newGrid && currentGrid) {
          newGrid.querySelectorAll('.ff-blog-card').forEach((article) => {
            currentGrid.appendChild(article);
          });
        }

        // Update sentinel with next page data
        /** @type {HTMLElement|null} */
        const newSentinel = newBlock?.querySelector('.ff-blog-infinite-sentinel') ?? null;
        if (newSentinel?.dataset.ffNextPage) {
          sentinel.dataset.ffNextPage = newSentinel.dataset.ffNextPage;
          sentinel.dataset.ffCurrentPage = newSentinel.dataset.ffCurrentPage ?? '';
          sentinel.dataset.ffTotalPages = newSentinel.dataset.ffTotalPages ?? '';
        } else {
          delete sentinel.dataset.ffNextPage;
        }

        this.ffObserveSentinel();
      } catch (error) {
        console.error('Failed to load more articles:', error);
      } finally {
        this.ffIsLoadingMore = false;
        sentinel.classList.remove('ff-loading');
      }
    };

    // ── AJAX Numbered Pagination ─────────────────────────────

    ffInitPaginationLinks = () => {
      this.addEventListener('click', (e) => {
        const link = /** @type {HTMLElement} */ (e.target).closest(
          '.ff-pagination-actions a[href]'
        );
        if (!link) return;

        /** @type {HTMLElement|null} */
        const paginationArea = link.closest('.ff-blog-pagination-area');
        if (paginationArea?.dataset.ffAjaxPagination === 'false') return;

        e.preventDefault();
        const pageUrl = link.getAttribute('href');
        if (!pageUrl || pageUrl === '#') return;

        this.ffLoadPage(pageUrl);
      });
    };

    /**
     * @param {string} url
     */
    ffLoadPage = async (url) => {
      if (this.ffAbortController) {
        this.ffAbortController.abort();
      }
      this.ffAbortController = new AbortController();
      const { signal } = this.ffAbortController;

      this.classList.add('ff-shimmer');
      this.ffScrollToGrid();

      try {
        const pageUrl = new URL(url, window.location.origin);
        const response = await fetch(pageUrl, { signal });
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html');

        const thisId = this.id;
        /** @type {HTMLElement|null} */
        const newBlock = html.getElementById(thisId);

        if (newBlock) {
          // Replace grid
          const newGrid = newBlock.querySelector('.ff-blog-grid-js');
          const currentGrid = this.querySelector('.ff-blog-grid-js');
          if (newGrid && currentGrid) {
            currentGrid.replaceWith(newGrid);
          }

          // Replace pagination area
          const newPagination = newBlock.querySelector('.ff-blog-pagination-area');
          const currentPagination = this.querySelector('.ff-blog-pagination-area');
          if (newPagination && currentPagination) {
            currentPagination.replaceWith(newPagination);
          } else if (!newPagination && currentPagination) {
            currentPagination.remove();
          } else if (newPagination && !currentPagination) {
            this.appendChild(newPagination);
          }
        }

        history.pushState(null, '', pageUrl.href);
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        console.error('Failed to load blog page:', error);
      } finally {
        if (!signal.aborted) {
          requestAnimationFrame(() => {
            this.classList.remove('ff-shimmer');
          });
        }
      }
    };

    ffScrollToGrid = () => {
      const gridRect = this.getBoundingClientRect();
      const scrollTop = window.scrollY + gridRect.top;
      const headerOffset = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0',
        10
      );

      window.scrollTo({
        top: Math.max(0, scrollTop - headerOffset - 20),
        behavior: 'smooth',
      });
    };
  }

  customElements.define('ff-blog-pagination', FfBlogPagination);
}
